import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';


import PolicyTemplateLibrary from './components/PolicyTemplateLibrary';
import CustomizationWorkspace from './components/CustomizationWorkspace';
import VersionControlPanel from './components/VersionControlPanel';

const PolicyLibraryManagement = () => {
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [policyVersions, setPolicyVersions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customizations, setCustomizations] = useState({});

  // Policy templates with PNG statutory baselines
  const policyTemplates = [
    {
      id: 1,
      name: 'Leave Policy',
      category: 'leave',
      description: 'Annual leave, sick leave, and maternity leave entitlements per PNG Employment Act 1978',
      statutoryBaseline: true,
      lastUpdated: '2026-01-10',
      clauses: [
        {
          id: 'leave-1',
          title: 'Annual Leave Entitlement',
          content: 'Employees are entitled to 4 weeks annual leave per year',
          customizable: true,
          minimum: '4 weeks',
          current: '4 weeks'
        },
        {
          id: 'leave-2',
          title: 'Sick Leave',
          content: 'Employees are entitled to 2 weeks sick leave per year',
          customizable: true,
          minimum: '2 weeks',
          current: '2 weeks'
        },
        {
          id: 'leave-3',
          title: 'Maternity Leave',
          content: 'Female employees are entitled to 6 weeks maternity leave',
          customizable: false,
          minimum: '6 weeks',
          current: '6 weeks'
        }
      ]
    },
    {
      id: 2,
      name: 'Overtime Policy',
      category: 'payroll',
      description: 'Overtime rates and calculation methods compliant with PNG minimum wage regulations',
      statutoryBaseline: true,
      lastUpdated: '2026-01-08',
      clauses: [
        {
          id: 'ot-1',
          title: 'Overtime Rate',
          content: 'Overtime is paid at 1.5x regular hourly rate',
          customizable: true,
          minimum: '1.5x',
          current: '1.5x'
        },
        {
          id: 'ot-2',
          title: 'Weekend Work',
          content: 'Weekend work is paid at 2x regular hourly rate',
          customizable: true,
          minimum: '1.5x',
          current: '2x'
        }
      ]
    },
    {
      id: 3,
      name: 'Termination Policy',
      category: 'employment',
      description: 'Notice periods and termination procedures per PNG Employment Act',
      statutoryBaseline: true,
      lastUpdated: '2026-01-05',
      clauses: [
        {
          id: 'term-1',
          title: 'Notice Period',
          content: 'Minimum 2 weeks notice required for termination',
          customizable: true,
          minimum: '2 weeks',
          current: '4 weeks'
        },
        {
          id: 'term-2',
          title: 'Severance Pay',
          content: 'Severance calculated at 2 weeks pay per year of service',
          customizable: false,
          minimum: '2 weeks per year',
          current: '2 weeks per year'
        }
      ]
    },
    {
      id: 4,
      name: 'Benefits Policy',
      category: 'benefits',
      description: 'Employee benefits including NASFUND, housing allowance, and medical coverage',
      statutoryBaseline: true,
      lastUpdated: '2026-01-12',
      clauses: [
        {
          id: 'ben-1',
          title: 'NASFUND Contribution',
          content: 'Employer contributes 8.4% to NASFUND',
          customizable: false,
          minimum: '8.4%',
          current: '8.4%'
        },
        {
          id: 'ben-2',
          title: 'Housing Allowance',
          content: 'Housing allowance of K500 per month',
          customizable: true,
          minimum: 'K0',
          current: 'K500'
        }
      ]
    },
    {
      id: 5,
      name: 'Work Hours Policy',
      category: 'employment',
      description: 'Standard work hours and break entitlements',
      statutoryBaseline: true,
      lastUpdated: '2026-01-03',
      clauses: [
        {
          id: 'hours-1',
          title: 'Standard Work Week',
          content: 'Standard work week is 40 hours',
          customizable: false,
          minimum: '40 hours',
          current: '40 hours'
        },
        {
          id: 'hours-2',
          title: 'Meal Breaks',
          content: 'Employees receive 1 hour meal break per day',
          customizable: true,
          minimum: '30 minutes',
          current: '1 hour'
        }
      ]
    },
    {
      id: 6,
      name: 'Probation Policy',
      category: 'employment',
      description: 'Probationary period requirements and evaluation procedures',
      statutoryBaseline: true,
      lastUpdated: '2025-12-28',
      clauses: [
        {
          id: 'prob-1',
          title: 'Probation Duration',
          content: 'Probation period is 3 months',
          customizable: true,
          minimum: '1 month',
          current: '3 months'
        }
      ]
    }
  ];

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedPolicy) {
      loadPolicyVersions(selectedPolicy?.id);
      loadCustomizations(selectedPolicy?.id);
    }
  }, [selectedPolicy]);

  const loadPolicyVersions = async (policyId) => {
    const mockVersions = [
      {
        id: 1,
        version: '1.0',
        date: '2025-12-01',
        author: 'System',
        changes: 'Initial statutory baseline from PNG Employment Act 1978',
        isBaseline: true
      },
      {
        id: 2,
        version: '1.1',
        date: '2026-01-10',
        author: 'HR Manager',
        changes: 'Increased notice period from 2 weeks to 4 weeks',
        isBaseline: false
      }
    ];
    setPolicyVersions(mockVersions);
  };

  const loadCustomizations = async (policyId) => {
    // Load saved customizations from localStorage or database
    const saved = localStorage.getItem(`policy-${policyId}`);
    if (saved) {
      setCustomizations(JSON.parse(saved));
    } else {
      setCustomizations({});
    }
  };

  const handlePolicySelect = (policy) => {
    setSelectedPolicy(policy);
  };

  const handlePolicyUpdate = async (updatedPolicy) => {
    try {
      setSaving(true);
      
      // Save customizations
      localStorage.setItem(`policy-${updatedPolicy?.id}`, JSON.stringify(updatedPolicy?.clauses));
      
      // Create new version
      const newVersion = {
        id: policyVersions?.length + 1,
        version: `1.${policyVersions?.length}`,
        date: new Date()?.toISOString()?.split('T')?.[0],
        author: 'Current User',
        changes: 'Policy customization updated',
        isBaseline: false
      };
      
      setPolicyVersions([newVersion, ...policyVersions]);
      setSelectedPolicy(updatedPolicy);
      
      alert('Policy customization saved successfully');
    } catch (error) {
      alert(`Failed to save policy: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToVersion = async (version) => {
    if (!confirm(`Revert policy to version ${version?.version}?`)) return;
    
    try {
      setSaving(true);
      // In real implementation, load version from database
      alert(`Policy reverted to version ${version?.version}`);
      loadPolicyVersions(selectedPolicy?.id);
    } catch (error) {
      alert(`Failed to revert policy: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedPolicy) {
      alert('Please select a policy to export');
      return;
    }

    try {
      setIsExporting(true);
      setExportFormat(format);
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      const filename = `${selectedPolicy?.name?.toLowerCase()?.replace(/\s+/g, '-')}-${timestamp}.${format}`;
      
      // Generate export content
      let content = `${selectedPolicy?.name}\n\n`;
      content += `${selectedPolicy?.description}\n\n`;
      content += `Last Updated: ${selectedPolicy?.lastUpdated}\n\n`;
      content += `Clauses:\n`;
      selectedPolicy?.clauses?.forEach((clause, index) => {
        content += `\n${index + 1}. ${clause?.title}\n`;
        content += `   ${clause?.content}\n`;
        content += `   Minimum: ${clause?.minimum}\n`;
        content += `   Current: ${clause?.current}\n`;
      });
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Policy exported as ${filename}`);
    } catch (error) {
      alert(`Export failed: ${error?.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const filteredPolicies = policyTemplates?.filter(policy => {
    const matchesSearch = policy?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         policy?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || policy?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />

      <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3">
                  Policy Library Management
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Customize organizational policies while maintaining PNG Employment Act 1978 statutory compliance
                </p>
              </div>
              
              {selectedPolicy && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => handleExport('pdf')}
                    loading={isExporting && exportFormat === 'pdf'}
                    iconName="Download"
                  >
                    Export PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={() => handleExport('docx')}
                    loading={isExporting && exportFormat === 'docx'}
                    iconName="FileText"
                  >
                    Export Word
                  </Button>
                </div>
              )}
            </div>

            <div className="mb-6">
              <Input
                type="text"
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="max-w-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4">
              <PolicyTemplateLibrary
                policies={filteredPolicies}
                selectedPolicy={selectedPolicy}
                onPolicySelect={handlePolicySelect}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
              />
            </div>

            <div className="lg:col-span-8">
              {selectedPolicy ? (
                <div className="space-y-6">
                  <CustomizationWorkspace
                    policy={selectedPolicy}
                    onPolicyUpdate={handlePolicyUpdate}
                    loading={saving}
                  />
                  <VersionControlPanel
                    versions={policyVersions}
                    selectedPolicy={selectedPolicy}
                    onRevert={handleRevertToVersion}
                    loading={saving}
                  />
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <p className="text-muted-foreground text-lg">Select a policy from the library to begin customization</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PolicyLibraryManagement;